import { DriverAuthenticationClient, DriverAuthenticationManager } from '@frontend/authentication-v2';
import { EquipmentStateName, IEquipment } from '@frontend/equipment/types';
import { ErrorHandler } from '@frontend/error-handler';
import { CommonMessage } from '@frontend/lang';
import { Logger } from '@frontend/logger';
import { TransactionType } from '@frontend/transaction/types';
import { IUser } from '@frontend/user/types';
import { ScreenTrigger } from '@frontend/wizard/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { useIdentification } from '../../../hooks';
import { startUserSession } from '../../../redux/terminal-navigation-slice';
import { useWizardPackages } from '../../hooks/useWizardPackages';
import { useWizardSettings } from '../../hooks/useWizardSettings';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { SlotOptionsByProduct, chooseBestSingleSlotOption, fetchAvailableSlotOptions } from '../../utils/available-slot';
import { submitLendingReturnTransaction } from '../../utils/submit-lending-return-transaction';
import { toError } from '../../utils/to-error';
import { useKitForProduct } from '../kit-return/shared/use-kit-for-product';
import { KitReturnEntry, KitReturnStatus } from '../kit-return/shared/use-kit-return';
import { useScreen } from '../useScreen';

const trimScansToKitQuantities = (scannedEquipments: IEquipment[], requiredByProductId: Map<string, number>): IEquipment[] => {
    const countByProductId = new Map<string, number>();
    return scannedEquipments.filter((equipment) => {
        const required = requiredByProductId.get(equipment.product_id) ?? 0;
        const count = countByProductId.get(equipment.product_id) ?? 0;
        if (count >= required) return false;
        countByProductId.set(equipment.product_id, count + 1);
        return true;
    });
};

const getStoredKitReturnEquipments = (data: Record<string, any> | undefined): IEquipment[] | null => {
    if (!Object.prototype.hasOwnProperty.call(data ?? {}, 'kitReturnEquipments')) return null;
    return Array.isArray(data?.kitReturnEquipments) ? (data.kitReturnEquipments as IEquipment[]) : [];
};

const hasSameEquipmentIds = (left: IEquipment[], right: IEquipment[]): boolean => {
    if (left.length !== right.length) return false;
    return left.every((equipment, index) => equipment.id === right[index]?.id);
};

const countEquipmentsByProductId = (equipments: IEquipment[]): Map<string, number> => {
    const counts = new Map<string, number>();
    for (const equipment of equipments) {
        counts.set(equipment.product_id, (counts.get(equipment.product_id) ?? 0) + 1);
    }
    return counts;
};

interface SlotOptionsForEquipments {
    optionGroups: SlotOptionsByProduct[];
    addedProductQuantities: Map<string, number>;
}

interface EquipmentSlotLookup {
    productId: string;
    equipmentState: EquipmentStateName;
}

const getEquipmentSlotLookups = (equipments: IEquipment[]): EquipmentSlotLookup[] => [
    ...new Map(
        equipments.map((equipment) => [
            `${equipment.product_id}:${equipment.state}`,
            {
                productId: equipment.product_id,
                equipmentState: equipment.state
            }
        ])
    ).values()
];

export const useConfirmKitReturn = () => {
    const intl = useIntl();
    const location = useLocation();
    const { store, navigationState, dispatch, screen_type, updateWizardData, onBack, onSubmit } = useScreen();
    const locationEquipment = (location.state as { equipment?: IEquipment } | null)?.equipment ?? null;
    const wizardEquipment = store?.data?.equipment as IEquipment | undefined;
    const stateEquipment = locationEquipment ?? wizardEquipment ?? null;

    const { entries, initialScans, isLoading, loadError, retryLoad } = useKitForProduct(stateEquipment?.product_id, stateEquipment);

    const [scannedEquipments, setScannedEquipments] = useState<IEquipment[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [errorTitle, setErrorTitle] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isAuthenticatingUser, setIsAuthenticatingUser] = useState<boolean>(false);
    const [isCheckingSlotAvailability, setIsCheckingSlotAvailability] = useState<boolean>(false);
    const [requiresAuthenticationForSlot, setRequiresAuthenticationForSlot] = useState<boolean>(false);
    const scannedEquipmentsRef = useRef<IEquipment[]>([]);
    const isSubmittingRef = useRef<boolean>(false);
    const equipmentScanQueueRef = useRef<Promise<void>>(Promise.resolve());
    const authenticatedTargetSlotIdRef = useRef<string | null>(null);
    const pendingBlockedEquipmentRef = useRef<IEquipment | null>(null);
    const storedScans = getStoredKitReturnEquipments(store?.data);
    const { transactionWorkflowId, dropOffPackageWorkflowId } = useWizardSettings(store);

    const transactionContext = useWizardTransaction({
        store,
        wizardId: store?.wizard.id,
        autoCreate: false,
        autoFetch: false
    });

    const { setPackages, syncPackages } = useWizardPackages({
        store,
        wizardId: store?.wizard.id,
        autoFetch: false,
        transactionContext
    });

    const requiredByProductId = useMemo(() => {
        const map = new Map<string, number>();
        for (const entry of entries) map.set(entry.product.id, entry.quantity);
        return map;
    }, [entries]);
    const kitProductIds = useMemo(() => [...requiredByProductId.keys()], [requiredByProductId]);
    const needBadgeMessage = intl.formatMessage({
        id: 'kiosk.wizard.confirm-kit-return.need-badge-for-slot',
        defaultMessage: 'No empty SLOT is available. Scan your badge to continue with SLOT selection.'
    });
    const noSingleSlotMessage = intl.formatMessage({
        id: 'kiosk.wizard.confirm-kit-return.no-single-slot',
        defaultMessage: 'These items no longer fit in one SLOT. Remove an item to continue.'
    });
    const scanBadgeOrSkipItemMessage = intl.formatMessage({
        id: 'kiosk.wizard.confirm-kit-return.scan-badge-or-skip-item',
        defaultMessage: 'This item cannot be returned together with the currently scanned items. Scan your badge to continue, or leave this item out.'
    });
    const cannotScanItemTitle = intl.formatMessage({
        id: 'kiosk.wizard.kit-return-detail.cannot-scan-title',
        defaultMessage: 'Cannot scan item'
    });
    const noSuitableSlotTitle = intl.formatMessage({
        id: 'kiosk.wizard.kit-return-detail.no-suitable-slot-title',
        defaultMessage: 'No suitable SLOT'
    });
    const authenticationRequiredTitle = intl.formatMessage({
        id: 'kiosk.wizard.kit-return-detail.badge-required-title',
        defaultMessage: 'Authentication required',
        description: 'Error modal title when kit return needs the user to authenticate to continue SLOT selection'
    });
    const cannotContinueTitle = intl.formatMessage({
        id: 'kiosk.wizard.kit-return-detail.cannot-continue-title',
        defaultMessage: 'Cannot continue',
        description: 'Fallback title shown when a kit return cannot continue'
    });
    const showError = useCallback((title: string, message: string) => {
        setErrorTitle(title);
        setError(message);
    }, []);
    const resetError = useCallback((options?: { clearPendingScan?: boolean }) => {
        setError(null);
        setErrorTitle(null);
        if (options?.clearPendingScan !== false) pendingBlockedEquipmentRef.current = null;
    }, []);

    const commitScannedEquipments = useCallback(
        (equipments: IEquipment[]) => {
            pendingBlockedEquipmentRef.current = null;
            scannedEquipmentsRef.current = equipments;
            setScannedEquipments(equipments);
            updateWizardData({ kitReturnEquipments: equipments });
        },
        [updateWizardData]
    );

    useEffect(() => {
        if (isLoading || entries.length === 0) return;

        const next = trimScansToKitQuantities(storedScans ?? initialScans, requiredByProductId);
        scannedEquipmentsRef.current = next;
        setScannedEquipments(next);

        if (storedScans === null || !hasSameEquipmentIds(next, storedScans)) {
            updateWizardData({ kitReturnEquipments: next });
        }
        // Rehydrate when the kit data changes. `updateWizardData` is intentionally omitted because it is not memoized by useScreen.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entries.length, initialScans, isLoading, requiredByProductId, store?.data?.kitReturnEquipments]);

    const scansByProductId = useMemo(() => {
        const map = new Map<string, IEquipment[]>();
        for (const equipment of scannedEquipments) {
            const existing = map.get(equipment.product_id);
            map.set(equipment.product_id, existing ? [...existing, equipment] : [equipment]);
        }
        return map;
    }, [scannedEquipments]);

    const status: KitReturnStatus = useMemo(() => {
        if (isLoading) return { kind: 'loading' };
        if (loadError) return { kind: 'load-error' };
        if (entries.length === 0) return { kind: 'empty' };
        return { kind: 'ready' };
    }, [entries.length, isLoading, loadError]);

    const requiredRef = useRef<Map<string, number>>(requiredByProductId);
    requiredRef.current = requiredByProductId;

    const chooseSingleSlotOption = useCallback(
        (
            slotOptions: SlotOptionsForEquipments,
            options: {
                requireEmpty?: boolean;
                allowEmptyFallback?: boolean;
            }
        ) =>
            chooseBestSingleSlotOption(slotOptions.optionGroups, {
                addedProductQuantities: slotOptions.addedProductQuantities,
                productQuantityLimits: requiredRef.current,
                allowedProductIds: kitProductIds,
                requireEmpty: options.requireEmpty,
                allowEmptyFallback: options.allowEmptyFallback
            }),
        [kitProductIds]
    );

    const fetchSlotOptionsForEquipments = useCallback(
        async (equipments: IEquipment[], options: { slotId?: string | null } = {}): Promise<SlotOptionsForEquipments | null> => {
            const accountId = navigationState.iot?.account_id;
            const spotId = navigationState.iot?.spot_id;
            if (!accountId || !spotId) return null;

            const addedProductQuantities = countEquipmentsByProductId(equipments);
            const equipmentSlotLookups = getEquipmentSlotLookups(equipments);
            if (equipmentSlotLookups.length === 0) return null;

            const optionGroups: SlotOptionsByProduct[] = await Promise.all(
                equipmentSlotLookups.map(async ({ productId, equipmentState }) => {
                    const availableOptions = await fetchAvailableSlotOptions(
                        accountId,
                        spotId,
                        {
                            account_id: accountId,
                            application: store?.functionality?.application,
                            feature: store?.functionality?.feature,
                            functionality_id: store?.functionality?.id,
                            product_id: productId,
                            equipment_state: equipmentState,
                            stock_quantity: addedProductQuantities.get(productId) ?? 1,
                            transaction_type: TransactionType.LENDING
                        },
                        {
                            sortByContent: true,
                            excludedProductId: productId,
                            excludedProductQuantityLimits: requiredRef.current
                        }
                    );

                    return {
                        productId,
                        options: options.slotId ? availableOptions.filter((option) => option.slot.id === options.slotId) : availableOptions
                    };
                })
            );

            return { optionGroups, addedProductQuantities };
        },
        [
            navigationState.iot?.account_id,
            navigationState.iot?.spot_id,
            store?.functionality?.application,
            store?.functionality?.feature,
            store?.functionality?.id
        ]
    );

    const fetchSingleSlotOptionForEquipments = useCallback(
        async (
            equipments: IEquipment[],
            options: {
                requireEmpty?: boolean;
                allowEmptyFallback?: boolean;
                slotId?: string | null;
            }
        ) => {
            const slotOptions = await fetchSlotOptionsForEquipments(equipments, { slotId: options.slotId });
            if (!slotOptions) return null;

            return chooseSingleSlotOption(slotOptions, options);
        },
        [chooseSingleSlotOption, fetchSlotOptionsForEquipments]
    );

    useEffect(() => {
        if (status.kind !== 'ready') return;
        if (!navigationState.iot?.account_id || !navigationState.iot.spot_id) return;
        if (navigationState.user) {
            setRequiresAuthenticationForSlot(false);
            return;
        }
        if (scannedEquipments.length === 0) {
            setRequiresAuthenticationForSlot(false);
            return;
        }

        authenticatedTargetSlotIdRef.current = null;
        let cancelled = false;
        const run = async () => {
            setIsCheckingSlotAvailability(true);
            try {
                const slotOptions = await fetchSlotOptionsForEquipments(scannedEquipments);
                const emptySlotOption = slotOptions ? chooseSingleSlotOption(slotOptions, { requireEmpty: true }) : null;
                if (cancelled) return;

                if (emptySlotOption) {
                    setRequiresAuthenticationForSlot(false);
                    return;
                }

                const authenticatedSlotOption = slotOptions ? chooseSingleSlotOption(slotOptions, { allowEmptyFallback: true }) : null;
                setRequiresAuthenticationForSlot(true);
                showError(
                    authenticatedSlotOption ? authenticationRequiredTitle : noSuitableSlotTitle,
                    authenticatedSlotOption ? needBadgeMessage : noSingleSlotMessage
                );
            } catch (e: unknown) {
                if (!cancelled) ErrorHandler.handleError({ error: toError(e), entity_type: CommonMessage.OBJECTS.SLOT.DEFAULT });
            } finally {
                if (!cancelled) setIsCheckingSlotAvailability(false);
            }
        };

        run();

        return () => {
            cancelled = true;
        };
    }, [
        chooseSingleSlotOption,
        fetchSlotOptionsForEquipments,
        navigationState.iot?.account_id,
        navigationState.iot?.spot_id,
        navigationState.user,
        scannedEquipments,
        needBadgeMessage,
        noSingleSlotMessage,
        noSuitableSlotTitle,
        authenticationRequiredTitle,
        showError,
        status.kind
    ]);

    const validateAndAddEquipment = useCallback(
        async (equipment: IEquipment) => {
            const current = scannedEquipmentsRef.current;
            const required = requiredRef.current;

            if (current.some((e) => e.id === equipment.id)) {
                showError(
                    cannotScanItemTitle,
                    intl.formatMessage({
                        id: 'kiosk.wizard.kit-return-detail.already-scanned',
                        defaultMessage: 'This item was already scanned. Remove it from the list if you want to scan it again.'
                    })
                );
                return;
            }

            const requiredCount = required.get(equipment.product_id) ?? 0;
            if (requiredCount === 0) {
                showError(
                    cannotScanItemTitle,
                    intl.formatMessage({
                        id: 'kiosk.wizard.kit-return-detail.not-in-kit',
                        defaultMessage: 'This item is not part of this kit. Please scan an item from the kit.'
                    })
                );
                return;
            }

            const currentCount = current.filter((e) => e.product_id === equipment.product_id).length;
            if (currentCount >= requiredCount) {
                showError(
                    cannotScanItemTitle,
                    intl.formatMessage({
                        id: 'kiosk.wizard.kit-return-detail.already-complete',
                        defaultMessage:
                            'The required amount for this product is already complete. Remove an item first if this scan should replace another one.'
                    })
                );
                return;
            }

            const next = [...current, equipment];
            setIsCheckingSlotAvailability(true);
            try {
                if (navigationState.user) {
                    const bestSlotOption = await fetchSingleSlotOptionForEquipments(next, {
                        allowEmptyFallback: !authenticatedTargetSlotIdRef.current,
                        slotId: authenticatedTargetSlotIdRef.current
                    });
                    if (!bestSlotOption) {
                        showError(noSuitableSlotTitle, noSingleSlotMessage);
                        return;
                    }
                    authenticatedTargetSlotIdRef.current = bestSlotOption.slot.id;
                } else {
                    const slotOptions = await fetchSlotOptionsForEquipments(next);
                    const emptySlotOption = slotOptions ? chooseSingleSlotOption(slotOptions, { requireEmpty: true }) : null;
                    if (!emptySlotOption) {
                        const authenticatedSlotOption = slotOptions ? chooseSingleSlotOption(slotOptions, { allowEmptyFallback: true }) : null;
                        if (!authenticatedSlotOption) {
                            showError(noSuitableSlotTitle, noSingleSlotMessage);
                            return;
                        }

                        pendingBlockedEquipmentRef.current = equipment;
                        showError(authenticationRequiredTitle, scanBadgeOrSkipItemMessage);
                        return;
                    }
                }
            } finally {
                setIsCheckingSlotAvailability(false);
            }

            commitScannedEquipments(next);
        },
        [
            commitScannedEquipments,
            chooseSingleSlotOption,
            fetchSingleSlotOptionForEquipments,
            fetchSlotOptionsForEquipments,
            intl,
            navigationState.user,
            noSingleSlotMessage,
            scanBadgeOrSkipItemMessage,
            showError,
            cannotScanItemTitle,
            authenticationRequiredTitle,
            noSuitableSlotTitle
        ]
    );

    const addPendingScanAfterAuthentication = useCallback(async () => {
        const pendingEquipment = pendingBlockedEquipmentRef.current;
        if (!pendingEquipment) return;

        const current = scannedEquipmentsRef.current;
        if (current.some((equipment) => equipment.id === pendingEquipment.id)) {
            pendingBlockedEquipmentRef.current = null;
            return;
        }

        const requiredCount = requiredRef.current.get(pendingEquipment.product_id) ?? 0;
        const currentCount = current.filter((equipment) => equipment.product_id === pendingEquipment.product_id).length;
        if (requiredCount === 0 || currentCount >= requiredCount) {
            pendingBlockedEquipmentRef.current = null;
            return;
        }

        const next = [...current, pendingEquipment];
        setIsCheckingSlotAvailability(true);
        try {
            const bestSlotOption = await fetchSingleSlotOptionForEquipments(next, {
                allowEmptyFallback: !authenticatedTargetSlotIdRef.current,
                slotId: authenticatedTargetSlotIdRef.current
            });
            if (!bestSlotOption) {
                showError(noSuitableSlotTitle, noSingleSlotMessage);
                return;
            }

            authenticatedTargetSlotIdRef.current = bestSlotOption.slot.id;
            commitScannedEquipments(next);
        } finally {
            setIsCheckingSlotAvailability(false);
        }
    }, [commitScannedEquipments, fetchSingleSlotOptionForEquipments, noSingleSlotMessage, noSuitableSlotTitle, showError]);

    const handleEquipmentIdentified = useCallback(
        (equipment: IEquipment) => {
            equipmentScanQueueRef.current = equipmentScanQueueRef.current
                .then(() => validateAndAddEquipment(equipment))
                .catch((e: unknown) => {
                    ErrorHandler.handleError({ error: toError(e), entity_type: CommonMessage.OBJECTS.EQUIPMENT.DEFAULT });
                });
        },
        [validateAndAddEquipment]
    );

    const handleUserIdentified = useCallback(
        (user: IUser) => {
            if (navigationState.user) return;

            const accountId = navigationState.iot?.account_id;
            const iotId = navigationState.iot?.id;
            if (!accountId || !iotId) {
                Logger.error('Missing account_id or iot_id, thus cannot login the user.');
                showError(
                    cannotContinueTitle,
                    intl.formatMessage({
                        id: 'kiosk.wizard.confirm-kit-return.login-error',
                        defaultMessage: 'We could not authenticate you. Please try again.'
                    })
                );
                return;
            }

            const authenticate = async () => {
                setIsAuthenticatingUser(true);
                resetError({ clearPendingScan: false });
                try {
                    const token = await DriverAuthenticationManager.getInstance().waitForToken();
                    let auth = await DriverAuthenticationClient.authorizeUser(accountId, iotId, user.id, token).catch(() => null);
                    if (!auth) {
                        const info = DriverAuthenticationManager.getInstance().provisionInfo;
                        if (info) {
                            await DriverAuthenticationManager.getInstance().authenticate(info);
                            auth = await DriverAuthenticationClient.authorizeUser(accountId, iotId, user.id, token).catch(() => null);
                        }
                    }

                    if (!auth) {
                        showError(
                            cannotContinueTitle,
                            intl.formatMessage({
                                id: 'kiosk.wizard.confirm-kit-return.login-error',
                                defaultMessage: 'We could not authenticate you. Please try again.'
                            })
                        );
                        return;
                    }

                    dispatch(startUserSession({ id: user.id, accountId, auth, user }));
                    setRequiresAuthenticationForSlot(false);
                    await addPendingScanAfterAuthentication();
                } catch (e: unknown) {
                    Logger.error('Error during identification:', e);
                    showError(
                        cannotContinueTitle,
                        intl.formatMessage({
                            id: 'kiosk.wizard.confirm-kit-return.login-error',
                            defaultMessage: 'We could not authenticate you. Please try again.'
                        })
                    );
                } finally {
                    setIsAuthenticatingUser(false);
                }
            };

            authenticate();
        },
        [
            addPendingScanAfterAuthentication,
            dispatch,
            intl,
            navigationState.iot?.account_id,
            navigationState.iot?.id,
            navigationState.user,
            resetError,
            showError,
            cannotContinueTitle
        ]
    );

    useIdentification({
        id: 'CONFIRM_KIT_RETURN_IDENTIFICATION',
        onNoMatchingIdentificationFound: () => {
            showError(
                cannotContinueTitle,
                intl.formatMessage({
                    id: 'kiosk.wizard.confirm-kit-return.identification-error',
                    defaultMessage: 'No matching identification was found. Please try again.'
                })
            );
        },
        onUnlinkedIdentificationFound: () => {
            showError(
                cannotContinueTitle,
                intl.formatMessage({
                    id: 'kiosk.wizard.confirm-kit-return.unlinked-identification',
                    description: 'Error shown when a scanned code is recognized but not linked to anything',
                    defaultMessage: 'This code was recognized but is not linked to anything yet. Please try a different code or contact support.'
                })
            );
        },
        onEquipmentIdentified: handleEquipmentIdentified,
        onUserIdentified: handleUserIdentified
    });

    const removeScan = useCallback(
        (equipmentId: string) => {
            const next = scannedEquipmentsRef.current.filter((equipment) => equipment.id !== equipmentId);
            scannedEquipmentsRef.current = next;
            if (next.length === 0) authenticatedTargetSlotIdRef.current = null;
            setScannedEquipments(next);
            updateWizardData({ kitReturnEquipments: next });
        },
        [updateWizardData]
    );

    const submitDisabled =
        status.kind !== 'ready' ||
        scannedEquipments.length === 0 ||
        isSubmitting ||
        isAuthenticatingUser ||
        isCheckingSlotAvailability ||
        (!navigationState.user && requiresAuthenticationForSlot);
    const authenticatedSlotSelectionStep = useMemo(
        () => store?.steps.find((step) => step.screen === screen_type && step.trigger === ScreenTrigger.SUBMIT_FALSE),
        [screen_type, store?.steps]
    );

    const handleSubmit = useCallback(async () => {
        if (submitDisabled) return;
        if (isSubmittingRef.current) return;

        const trimmedScans = trimScansToKitQuantities(scannedEquipments, requiredRef.current);
        if (trimmedScans.length === 0) {
            ErrorHandler.handleError({
                error: new Error('Cannot confirm a kit return without scanned equipment'),
                entity_type: CommonMessage.OBJECTS.TRANSACTION.DEFAULT
            });
            return;
        }
        updateWizardData({
            equipment: stateEquipment ?? trimmedScans[0],
            kitReturnEquipments: trimmedScans,
            kitReturnProductQuantityLimits: Object.fromEntries(requiredRef.current.entries())
        });

        if (navigationState.user && authenticatedSlotSelectionStep) {
            onSubmit(authenticatedSlotSelectionStep);
            return;
        }

        const iot = navigationState.iot;
        if (!iot?.account_id || !iot.spot_id) {
            ErrorHandler.handleError({
                error: new Error('Missing IoT context for kit return transaction'),
                entity_type: CommonMessage.OBJECTS.TRANSACTION.DEFAULT
            });
            return;
        }

        if (!transactionWorkflowId || !dropOffPackageWorkflowId) {
            ErrorHandler.handleError({
                error: new Error('Missing workflow settings for kit return transaction'),
                entity_type: CommonMessage.OBJECTS.TRANSACTION.DEFAULT
            });
            return;
        }

        const spotId = iot.spot_id;
        isSubmittingRef.current = true;
        setIsSubmitting(true);
        try {
            const productQuantityLimits = requiredRef.current;
            const singleSlotOption = await fetchSingleSlotOptionForEquipments(trimmedScans, {
                requireEmpty: !navigationState.user,
                allowEmptyFallback: !!navigationState.user
            });
            if (!singleSlotOption) {
                showError(
                    navigationState.user ? noSuitableSlotTitle : authenticationRequiredTitle,
                    navigationState.user ? noSingleSlotMessage : needBadgeMessage
                );
                return;
            }
            const selectedSlotByEquipmentId = new Map(trimmedScans.map((equipment) => [equipment.id, singleSlotOption.slot]));

            const result = await submitLendingReturnTransaction({
                iot: { ...iot, spot_id: spotId },
                equipments: trimmedScans,
                userId: stateEquipment?.user_id ?? trimmedScans[0]?.user_id ?? null,
                selectedSlotByEquipmentId,
                slotExcludedProductQuantityLimitsByEquipmentId: new Map(trimmedScans.map((equipment) => [equipment.id, productQuantityLimits])),
                settings: { transactionWorkflowId, dropOffPackageWorkflowId },
                application: store?.functionality?.application,
                feature: store?.functionality?.feature,
                functionality_id: store?.functionality?.id,
                transactionContext,
                setPackages,
                syncPackages
            });

            if (result.status === 'no-slot') {
                showError(
                    noSuitableSlotTitle,
                    intl.formatMessage({
                        id: 'kiosk.wizard.kit-return-detail.no-slot',
                        defaultMessage: 'These items do not fit in an available SLOT. Remove an item or ask an operator for help.'
                    })
                );
                return;
            }

            onSubmit();
        } catch (e: unknown) {
            ErrorHandler.handleError({ error: toError(e), entity_type: CommonMessage.OBJECTS.TRANSACTION.DEFAULT });
        } finally {
            setIsSubmitting(false);
            isSubmittingRef.current = false;
        }
    }, [
        authenticatedSlotSelectionStep,
        dropOffPackageWorkflowId,
        intl,
        navigationState.iot,
        navigationState.user,
        needBadgeMessage,
        noSingleSlotMessage,
        noSuitableSlotTitle,
        authenticationRequiredTitle,
        onSubmit,
        scannedEquipments,
        setPackages,
        stateEquipment,
        submitDisabled,
        syncPackages,
        transactionContext,
        transactionWorkflowId,
        updateWizardData,
        showError,
        fetchSingleSlotOptionForEquipments,
        store?.functionality?.application,
        store?.functionality?.feature,
        store?.functionality?.id
    ]);

    return {
        status,
        entries: entries as KitReturnEntry[],
        scannedEquipments,
        scansByProductId,
        isAuthenticated: !!navigationState.user,
        error,
        errorTitle,
        retryLoad,
        removeScan,
        resetError,
        submitDisabled,
        isSubmitting,
        isAuthenticatingUser,
        isCheckingSlotAvailability,
        requiresAuthenticationForSlot,
        onBack,
        onSubmit: handleSubmit
    };
};
